import i18n, { Module } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import TRANSLATION_AR from 'translations/arabic';
import TRANSLATION_BG from 'translations/bulgarian';
import TRANSLATION_CZ from 'translations/czech';
import TRANSLATION_DE from 'translations/german';
import TRANSLATION_EN from 'translations/english';
import TRANSLATION_EN_GB from 'translations/britishEnglish';
import TRANSLATION_ES from 'translations/spanish';
import TRANSLATION_FI from 'translations/finnish';
import TRANSLATION_FR from 'translations/french';
import TRANSLATION_ID from 'translations/indonesian';
import TRANSLATION_IT from 'translations/italian';
import TRANSLATION_JP from 'translations/japanese';
import TRANSLATION_KO from 'translations/korean';
import TRANSLATION_NL from 'translations/dutch';
import TRANSLATION_PL from 'translations/polish';
import TRANSLATION_PT from 'translations/portuguese';
import TRANSLATION_RO from 'translations/romanian';
import TRANSLATION_RU from 'translations/russian';
import TRANSLATION_SV from 'translations/swedish';
import TRANSLATION_TH from 'translations/thai';
import TRANSLATION_TR from 'translations/turkish';
import TRANSLATION_VI from 'translations/vietnamese';
import TRANSLATION_ZH_HANS from 'translations/simplifiedChinese';
import TRANSLATION_ZH_HANT from 'translations/traditionalChinese';

export const LanguageCodes = {
  AR: 'ar',
  BG: 'bg',
  CS: 'cs',
  CZ: 'cz',
  DE: 'de',
  EN: 'en',
  'en-GB': 'en-GB',
  ES: 'es',
  FI: 'fi',
  FR: 'fr',
  ID: 'id',
  IT: 'it',
  JA: 'ja',
  JP: 'jp',
  KO: 'ko',
  NL: 'nl',
  PL: 'pl',
  PT: 'pt',
  RO: 'ro',
  RU: 'ru',
  SV: 'sv',
  TH: 'th',
  TR: 'tr',
  VI: 'vi',
  ZH: 'zh',
  'zh-CN': 'zh-CN',
  'zh-HK': 'zh-HK',
  'zh-Hans': 'zh-Hans',
  'zh-Hant': 'zh-Hant',
  'zh-MO': 'zh-MO',
  'zh-SG': 'zh-SG',
  'zh-TW': 'zh-TW',
} as const;

export type Language = (typeof LanguageCodes)[keyof typeof LanguageCodes];

const translations: Record<
  Language,
  { translation: Record<string, string | object> }
> = {
  ar: { translation: TRANSLATION_AR },
  bg: { translation: TRANSLATION_BG },
  cs: { translation: TRANSLATION_CZ },
  cz: { translation: TRANSLATION_CZ },
  de: { translation: TRANSLATION_DE },
  en: { translation: TRANSLATION_EN },
  'en-GB': { translation: TRANSLATION_EN_GB },
  es: { translation: TRANSLATION_ES },
  fi: { translation: TRANSLATION_FI },
  fr: { translation: TRANSLATION_FR },
  id: { translation: TRANSLATION_ID },
  it: { translation: TRANSLATION_IT },
  ja: { translation: TRANSLATION_JP },
  jp: { translation: TRANSLATION_JP },
  ko: { translation: TRANSLATION_KO },
  nl: { translation: TRANSLATION_NL },
  pl: { translation: TRANSLATION_PL },
  pt: { translation: TRANSLATION_PT },
  ro: { translation: TRANSLATION_RO },
  ru: { translation: TRANSLATION_RU },
  sv: { translation: TRANSLATION_SV },
  th: { translation: TRANSLATION_TH },
  tr: { translation: TRANSLATION_TR },
  vi: { translation: TRANSLATION_VI },
  zh: { translation: TRANSLATION_ZH_HANS },
  'zh-CN': { translation: TRANSLATION_ZH_HANS },
  'zh-HK': { translation: TRANSLATION_ZH_HANT },
  'zh-Hans': { translation: TRANSLATION_ZH_HANS },
  'zh-Hant': { translation: TRANSLATION_ZH_HANT },
  'zh-MO': { translation: TRANSLATION_ZH_HANT },
  'zh-SG': { translation: TRANSLATION_ZH_HANS },
  'zh-TW': { translation: TRANSLATION_ZH_HANT },
};

// Custom detector to handle ?lang= or ?lng=
const customDetector = {
  name: 'customQueryDetector',
  lookup(): string | undefined {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get('lang') || urlParams.get('lng');
    return lang ?? undefined;
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(customDetector);

i18n
  .use(languageDetector as Module)
  .use(initReactI18next)
  .init({
    fallbackLng: LanguageCodes.EN,
    debug: process.env.NODE_ENV === 'development',
    resources: translations,
    // React already escapes; i18next escaping would double-encode ("Spa &amp; Wellness").
    interpolation: { escapeValue: false },
    detection: {
      order: [
        'customQueryDetector',
        'queryString',
        'path',
        'subdomain',
        'localStorage',
        'navigator',
      ],
    },
  });

export default i18n;
